import { strToU8, zipSync } from 'fflate';
import { CI_THEME, type ThemeColors, mix } from '@pst/shared';
import { type Cell, type Sheet, sheetNames } from './sheet';
import { currentColors } from '../theme';

/**
 * A minimal writer for Office Open XML workbooks (.xlsx): one worksheet per
 * sheet, a bold title and header, frozen header, column widths, numbers as
 * numbers with German-style formats. Written here rather than pulled in as a
 * large library; the format is an open ISO standard (ISO/IEC 29500).
 */
/** Control characters are not allowed in XML 1.0; tab, line feed and carriage return are. */
const stripControls = (s: string): string =>
  [...s].filter((ch) => { const c = ch.charCodeAt(0); return c >= 32 || c === 9 || c === 10 || c === 13; }).join('');

const esc = (s: string): string =>
  stripControls(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

const colName = (i: number): string => {
  let n = i + 1;
  let out = '';
  while (n > 0) {
    const r = (n - 1) % 26;
    out = String.fromCharCode(65 + r) + out;
    n = Math.floor((n - 1) / 26);
  }
  return out;
};

// Style indices in styles.xml: 0 plain, 1 bold, 2 title, 3 amount, 4 quantity, 5 int, 6 bold amount, 7 bold quantity, 8 bold int, 9 header, 10 subtitle, 11 header over numbers (right-aligned)
const numberStyle = { amount: 3, quantity: 4, int: 5 } as const;

function cellXml(ref: string, value: Cell, style: number): string {
  // Empty cells are written too, so the fine rule under each row does not break.
  if (value === null || value === '') return `<c r="${ref}" s="${style}"/>`;
  if (typeof value === 'number' && Number.isFinite(value)) return `<c r="${ref}" s="${style}"><v>${value}</v></c>`;
  return `<c r="${ref}" s="${style}" t="inlineStr"><is><t xml:space="preserve">${esc(String(value))}</t></is></c>`;
}

function sheetXml(sheet: Sheet): string {
  const rows: string[] = [];
  let r = 1;
  rows.push(`<row r="${r}">${cellXml(`A${r}`, sheet.title, 2)}</row>`);
  r++;
  if (sheet.subtitle) { rows.push(`<row r="${r}">${cellXml(`A${r}`, sheet.subtitle, 10)}</row>`); r++; }
  r++;
  const headerRow = r;
  rows.push(`<row r="${r}" ht="20" customHeight="1">${sheet.columns.map((c, i) => cellXml(`${colName(i)}${r}`, c.label, c.format ? 11 : 9)).join('')}</row>`);
  const bold = new Set(sheet.boldRows ?? []);
  sheet.rows.forEach((row, index) => {
    r++;
    const b = bold.has(index);
    rows.push(`<row r="${r}">${row.map((v, i) => {
      const f = sheet.columns[i]?.format;
      const style = typeof v === 'number' && f ? numberStyle[f] + (b ? 3 : 0) : b ? 1 : 0;
      return cellXml(`${colName(i)}${r}`, v, style);
    }).join('')}</row>`);
  });
  const widths = sheet.columns.map((c, i) => {
    const longest = Math.max(c.label.length, ...sheet.rows.slice(0, 500).map((row) => String(row[i] ?? '').length));
    return `<col min="${i + 1}" max="${i + 1}" width="${Math.min(60, Math.max(6, c.width ?? longest + 2))}" customWidth="1"/>`;
  });
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">
<sheetViews><sheetView workbookViewId="0"><pane ySplit="${headerRow}" topLeftCell="A${headerRow + 1}" activePane="bottomLeft" state="frozen"/></sheetView></sheetViews>
${widths.length ? `<cols>${widths.join('')}</cols>` : ''}
<sheetData>${rows.join('')}</sheetData>
<pageSetup orientation="landscape" paperSize="9" fitToWidth="1" fitToHeight="0"/>
</worksheet>`;
}

/** Office Open XML colour: opaque ARGB from #RRGGBB. */
const argb = (hex: string): string => `FF${hex.replace('#', '').toUpperCase()}`;

/**
 * The look of the office (CI V01): Arial, the CI's substitute for files that
 * are opened elsewhere, text in the text colour, the title in the main
 * colour, the header row white on the main colour, sums on its lightest
 * tint, fine rules in light grey.
 */
function styles(c: ThemeColors): string {
  const text = argb(c.text);
  const grey = argb(mix(c.text, '#FFFFFF', 0.3));
  const rule = argb(mix(c.text, '#FFFFFF', 0.8));
  const tint = argb(mix(c.primary, '#FFFFFF', 0.9));
  const font = (extra: string, size: number, colour: string): string => `<font>${extra}<sz val="${size}"/><color rgb="${colour}"/><name val="Arial"/></font>`;
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">
<numFmts count="2"><numFmt numFmtId="164" formatCode="#,##0.00"/><numFmt numFmtId="165" formatCode="#,##0.###"/></numFmts>
<fonts count="5">${font('', 10, text)}${font('<b/>', 10, text)}${font('<b/>', 14, argb(c.primary))}${font('<b/>', 10, 'FFFFFFFF')}${font('', 9, grey)}</fonts>
<fills count="4"><fill><patternFill patternType="none"/></fill><fill><patternFill patternType="gray125"/></fill><fill><patternFill patternType="solid"><fgColor rgb="${argb(c.primary)}"/><bgColor indexed="64"/></patternFill></fill><fill><patternFill patternType="solid"><fgColor rgb="${tint}"/><bgColor indexed="64"/></patternFill></fill></fills>
<borders count="2"><border><left/><right/><top/><bottom/><diagonal/></border><border><left/><right/><top/><bottom style="thin"><color rgb="${rule}"/></bottom><diagonal/></border></borders>
<cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>
<cellXfs count="12">
<xf numFmtId="0" fontId="0" fillId="0" borderId="1" xfId="0" applyFont="1" applyBorder="1"/>
<xf numFmtId="0" fontId="1" fillId="3" borderId="1" xfId="0" applyFont="1" applyFill="1" applyBorder="1"/>
<xf numFmtId="0" fontId="2" fillId="0" borderId="0" xfId="0" applyFont="1"/>
<xf numFmtId="164" fontId="0" fillId="0" borderId="1" xfId="0" applyNumberFormat="1" applyFont="1" applyBorder="1"/>
<xf numFmtId="165" fontId="0" fillId="0" borderId="1" xfId="0" applyNumberFormat="1" applyFont="1" applyBorder="1"/>
<xf numFmtId="3" fontId="0" fillId="0" borderId="1" xfId="0" applyNumberFormat="1" applyFont="1" applyBorder="1"/>
<xf numFmtId="164" fontId="1" fillId="3" borderId="1" xfId="0" applyNumberFormat="1" applyFont="1" applyFill="1" applyBorder="1"/>
<xf numFmtId="165" fontId="1" fillId="3" borderId="1" xfId="0" applyNumberFormat="1" applyFont="1" applyFill="1" applyBorder="1"/>
<xf numFmtId="3" fontId="1" fillId="3" borderId="1" xfId="0" applyNumberFormat="1" applyFont="1" applyFill="1" applyBorder="1"/>
<xf numFmtId="0" fontId="3" fillId="2" borderId="0" xfId="0" applyFont="1" applyFill="1" applyAlignment="1"><alignment vertical="center"/></xf>
<xf numFmtId="0" fontId="4" fillId="0" borderId="0" xfId="0" applyFont="1"/>
<xf numFmtId="0" fontId="3" fillId="2" borderId="0" xfId="0" applyFont="1" applyFill="1" applyAlignment="1"><alignment horizontal="right" vertical="center"/></xf>
</cellXfs>
</styleSheet>`;
}

export function writeXlsx(sheets: readonly Sheet[], colors: ThemeColors = CI_THEME): Uint8Array {
  const list = sheets.length ? sheets : [{ name: 'Leer', title: 'Keine Daten', columns: [], rows: [] }];
  const names = sheetNames(list.map((s) => s.name));
  const files: Record<string, Uint8Array> = {
    '[Content_Types].xml': strToU8(`<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">
<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>
<Default Extension="xml" ContentType="application/xml"/>
<Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>
<Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>
${list.map((_, i) => `<Override PartName="/xl/worksheets/sheet${i + 1}.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>`).join('\n')}
</Types>`),
    '_rels/.rels': strToU8(`<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/></Relationships>`),
    'xl/workbook.xml': strToU8(`<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships"><sheets>${names.map((n, i) => `<sheet name="${esc(n)}" sheetId="${i + 1}" r:id="rId${i + 1}"/>`).join('')}</sheets></workbook>`),
    'xl/_rels/workbook.xml.rels': strToU8(`<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">${list.map((_, i) => `<Relationship Id="rId${i + 1}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet${i + 1}.xml"/>`).join('')}<Relationship Id="rId${list.length + 1}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/></Relationships>`),
    'xl/styles.xml': strToU8(styles(colors)),
  };
  list.forEach((s, i) => { files[`xl/worksheets/sheet${i + 1}.xml`] = strToU8(sheetXml(s)); });
  return zipSync(files, { level: 6 });
}

export function downloadXlsx(sheets: readonly Sheet[], filename: string): void {
  const blob = new Blob([writeXlsx(sheets, currentColors()) as Uint8Array<ArrayBuffer>], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename.endsWith('.xlsx') ? filename : `${filename}.xlsx`;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
